// Iter 82.11 / Phase E3.2 Sprint 1 — Finance / Period Close Dashboard.
//
// "What hits the GL? Have we closed the period? What's the statutory tab?"
// Audience: Accounts, CFO. Period close + GL posting + statutory liabilities
// + cohort + MoM deltas + recent audit trail.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Scales, Bank, ArrowsClockwise, ShieldCheck, ClockCounterClockwise, ArrowRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const compact = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
    if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return inr(v);
};
const thisMonthYM = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const CLOSE_TONE = {
    Open: "bg-amber-100 text-amber-800",
    Closed: "bg-emerald-100 text-emerald-800",
    ReopenedTemporarily: "bg-rose-100 text-rose-800",
};

function MoMTile({ label, current, delta_pct, testId }) {
    const tone = (delta_pct ?? 0) > 5 || (delta_pct ?? 0) < -5 ? "text-rose-700" : "text-emerald-700";
    return (
        <div className="border rounded-lg bg-white p-4" data-testid={testId}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-xl font-mono font-semibold mt-0.5">{compact(current)}</div>
            <div className={`text-xs ${tone} mt-1 font-mono`}>
                {delta_pct > 0 ? "+" : ""}{Number(delta_pct).toFixed(2)}% MoM
            </div>
        </div>
    );
}

export default function PayrollFinanceDashboard() {
    const nav = useNavigate();
    const [periodMonth, setPeriodMonth] = useState(thisMonthYM());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/payroll/analytics/dashboard/finance", {
                params: { period_month: periodMonth },
            });
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load finance dashboard"));
        } finally {
            setLoading(false);
        }
    }, [periodMonth]);
    useEffect(() => { reload(); }, [reload]);

    if (loading) return <div className="p-6 text-slate-500" data-testid="fin-loading">Loading dashboard…</div>;
    if (!data) return <div className="p-6 text-rose-700">No data.</div>;

    const t = data.totals || {};
    const deltas = data.mom_deltas || {};
    const stat = data.statutory_liabilities || {};
    const cohort = data.cohort || {};

    return (
        <div className="space-y-4" data-testid="payroll-finance-dashboard">
            <PageHeader
                eyebrow="Payroll Dashboards · Finance & Period Close"
                title={`Finance · ${periodMonth}`}
                description="GL readiness + statutory liabilities + cohort + audit trail."
                actions={(
                    <div className="flex items-center gap-2">
                        <Input
                            type="month"
                            value={periodMonth}
                            onChange={(e) => setPeriodMonth(e.target.value)}
                            className="h-9 w-40"
                            data-testid="fin-period-picker"
                        />
                        <Button variant="outline" size="sm" onClick={reload} data-testid="fin-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                    </div>
                )}
            />

            {/* Close status banner */}
            <div className="border rounded-lg bg-white p-4 flex flex-wrap items-center gap-3" data-testid="fin-close-status">
                <Scales size={20} className="text-slate-400" />
                <div className="flex-1">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Period Close Status</div>
                    <div className="text-sm text-slate-700 mt-0.5">
                        {data.closed_at
                            ? `Closed on ${data.closed_at?.slice(0, 19).replace("T", " ")} by ${data.closed_by_email}`
                            : "Period is OPEN — close has not been initiated yet"}
                    </div>
                </div>
                <Badge className={`${CLOSE_TONE[data.period_close_status] || "bg-slate-100"} text-sm`} data-testid="fin-close-badge">
                    {data.period_close_status || "Open"}
                </Badge>
                <Badge variant="outline" className="text-xs" data-testid="fin-gl-status">
                    GL · {data.gl_posting_status || "Pending"}
                </Badge>
                {data.locked_runs_not_yet_posted_count > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid="fin-unposted-count">
                        {data.locked_runs_not_yet_posted_count} locked run(s) awaiting close
                    </Badge>
                )}
                <Button variant="outline" size="sm" onClick={() => nav("/admin/payroll/period-close")} data-testid="fin-deeplink-period-close">
                    <ArrowRight size={12} className="mr-1" /> Period Close
                </Button>
            </div>

            {/* MoM Variance tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MoMTile testId="fin-mom-gross" label="Gross Payroll" current={t.gross_earnings} delta_pct={deltas.gross_pct} />
                <MoMTile testId="fin-mom-net" label="Net Payable" current={t.net_pay} delta_pct={deltas.net_pct} />
                <MoMTile testId="fin-mom-deductions" label="Deductions" current={t.gross_deductions} delta_pct={deltas.deductions_pct} />
                <MoMTile testId="fin-mom-employer" label="Employer Contribs" current={t.employer_contributions} delta_pct={deltas.employer_pct} />
            </div>

            {/* Statutory liabilities */}
            <div className="border rounded-lg bg-white p-4" data-testid="fin-statutory">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={14} className="text-slate-400" />
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Statutory Liabilities ({periodMonth})</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border rounded p-3 bg-slate-50" data-testid="fin-stat-pf">
                        <div className="text-[10px] uppercase text-slate-500">PF (EE + ER)</div>
                        <div className="font-mono text-sm font-semibold mt-1">{inr((stat.pf_ee || 0) + (stat.pf_er || 0))}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">EE {inr(stat.pf_ee)} · ER {inr(stat.pf_er)}</div>
                    </div>
                    <div className="border rounded p-3 bg-slate-50" data-testid="fin-stat-esi">
                        <div className="text-[10px] uppercase text-slate-500">ESI (EE + ER)</div>
                        <div className="font-mono text-sm font-semibold mt-1">{inr((stat.esi_ee || 0) + (stat.esi_er || 0))}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">EE {inr(stat.esi_ee)} · ER {inr(stat.esi_er)}</div>
                    </div>
                    <div className="border rounded p-3 bg-slate-50" data-testid="fin-stat-pt">
                        <div className="text-[10px] uppercase text-slate-500">Professional Tax</div>
                        <div className="font-mono text-sm font-semibold mt-1">{inr(stat.pt)}</div>
                    </div>
                    <div className="border rounded p-3 bg-slate-50" data-testid="fin-stat-lwf">
                        <div className="text-[10px] uppercase text-slate-500">LWF</div>
                        <div className="font-mono text-sm font-semibold mt-1">{inr(stat.lwf)}</div>
                    </div>
                    <div className="border rounded p-3 bg-slate-50" data-testid="fin-stat-tds">
                        <div className="text-[10px] uppercase text-slate-500">TDS</div>
                        <div className="font-mono text-sm font-semibold mt-1">{inr(stat.tds)}</div>
                    </div>
                    <div className="border rounded p-3 bg-slate-50" data-testid="fin-stat-gratuity">
                        <div className="text-[10px] uppercase text-slate-500">Gratuity Accrual</div>
                        <div className="font-mono text-sm font-semibold mt-1">{inr(stat.gratuity_er)}</div>
                    </div>
                </div>
            </div>

            {/* Cohort */}
            <div className="border rounded-lg bg-white p-4" data-testid="fin-cohort">
                <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">Cohort This Month</div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
                    <div data-testid="fin-cohort-active"><div className="text-2xl font-mono font-semibold">{cohort.active || 0}</div><div className="text-[10px] uppercase text-slate-500">Active</div></div>
                    <div data-testid="fin-cohort-probation"><div className="text-2xl font-mono font-semibold">{cohort.probation || 0}</div><div className="text-[10px] uppercase text-slate-500">Probation</div></div>
                    <div data-testid="fin-cohort-notice"><div className="text-2xl font-mono font-semibold">{cohort.notice_period || 0}</div><div className="text-[10px] uppercase text-slate-500">Notice</div></div>
                    <div data-testid="fin-cohort-total"><div className="text-2xl font-mono font-semibold">{cohort.total_cohort_size || 0}</div><div className="text-[10px] uppercase text-slate-500">Cohort Size</div></div>
                    <div data-testid="fin-cohort-joiners"><div className="text-2xl font-mono text-emerald-700">{cohort.joiners || 0}</div><div className="text-[10px] uppercase text-slate-500">Joiners</div></div>
                    <div data-testid="fin-cohort-leavers"><div className="text-2xl font-mono text-zinc-600">{cohort.leavers || 0}</div><div className="text-[10px] uppercase text-slate-500">Leavers</div></div>
                </div>
            </div>

            {/* Recent audit */}
            <div className="border rounded-lg bg-white p-4" data-testid="fin-audit">
                <div className="flex items-center gap-2 mb-2">
                    <ClockCounterClockwise size={14} className="text-slate-400" />
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Recent Audit Trail (last 20 events)</div>
                </div>
                {(data.recent_audit || []).length === 0 ? (
                    <div className="text-sm text-slate-400" data-testid="fin-audit-empty">No audit events yet for this period.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500">
                            <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-left">Run</th><th className="px-3 py-2 text-left">By</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {(data.recent_audit || []).slice(0, 20).map((a, i) => (
                                <tr key={i} className="hover:bg-slate-50" data-testid={`fin-audit-row-${i}`}>
                                    <td className="px-3 py-2 text-xs font-mono">{a.created_at?.slice(0, 19).replace("T", " ")}</td>
                                    <td className="px-3 py-2 text-xs">{a.action}</td>
                                    <td className="px-3 py-2 text-xs font-mono"><a className="text-blue-600 hover:underline" href={`/admin/payroll/runs/${a.run_id}`}>{a.run_id}</a></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{a.actor_email}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
